/**
 * Authentication Redux Slice
 * - Handles login/logout state
 * - Manages OTP sending and verification
 * - Stores user data and tokens
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { sendOTPAPI, verifyOTPAPI } from "../../services/authAPI";

// Send OTP to mobile number
export const sendOTP = createAsyncThunk(
  "auth/sendOTP",
  async (mobile, { rejectWithValue }) => {
    try {
      const response = await sendOTPAPI(mobile);
      return { mobile, message: response.data.meta.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.meta?.message || "Failed to send OTP");
    }
  }
);

// Verify OTP and complete login
export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async ({ mobile, otp }, { rejectWithValue }) => {
    try {
      const response = await verifyOTPAPI(mobile, otp);
      const { token, user } = response.data.data;

      localStorage.setItem('authToken', token);
      return { token, user, message: response.data.meta.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.meta?.message || "Invalid OTP");
    }
  }
);

// Logout user
export const logout = createAsyncThunk(
  "auth/logout",
  async () => {
    localStorage.removeItem('authToken');
    return null;
  }
);

// Check if user has valid token
export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      return { token };
    }
    throw new Error('No token found');
  }
);

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  otpSent: false,
  mobile: null,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetOTP: (state) => {
      state.otpSent = false;
      state.mobile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Send OTP
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.otpSent = true;
        state.mobile = action.payload.mobile;
        state.error = null;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.otpSent = false;
        state.mobile = null;
        state.error = null;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.otpSent = false;
        state.mobile = null;
        state.error = null;
      })

      // Check Auth
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.token = action.payload.token;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
      });
  },
});

export const { clearError, resetOTP } = authSlice.actions;
export default authSlice.reducer;
